import { useContext } from "react";
import { HeadingText, ParagraphText } from "../../../Libraries";
import { getSignImage } from "../../Features/NewInvoiceBook/invoiceBookUtils";
import { formatNum } from "../../../oscar-pos-core/constants";
import { LanguageContext } from "../../../context/LanguageContext";
import styles from "./styles.module.css";
import { Box } from "@mui/material";

export const PdfColorPallete = [
  "#000000",
  "#27AAE1",
  "#E51A32",
  "#8D5983",
  "#F96A32",
  "#FFE7DC",
  "#D9D9D9",
];

const hasText = (v) => typeof v === "string" && v.trim().length > 0;

/** Ref / PO — input string, DB number, or empty */
const isRefEmpty = (v) => {
  if (v == null || v === "") return true;
  if (typeof v === "number") return Number.isNaN(v);
  const s = typeof v === "string" ? v.trim() : String(v).trim();
  return s === "" || s === "-";
};

const formatRefDisplay = (v) => {
  if (v == null || v === "") return "";
  if (typeof v === "number") return String(v);
  return typeof v === "string" ? v.trim() : String(v).trim();
};

/** Parse first number from formatted disc/tax (Rs. / % / Order invoice) */
const amountFromBillString = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Number.isNaN(v) ? 0 : Math.max(0, v);
  const s = String(v).replace(/,/g, "");
  const m = s.match(/(\d+\.?\d*)/);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  return Number.isNaN(n) ? 0 : n;
};

const hasBillDiscount = (bd) => {
  if (!bd) return false;
  if (bd.discValue != null && bd.discValue !== "") {
    return Number(bd.discValue) > 0;
  }
  return amountFromBillString(bd.disc) > 0;
};

const hasBillTax = (bd) => {
  if (!bd) return false;
  if (bd.taxValue != null && bd.taxValue !== "") {
    return Number(bd.taxValue) > 0;
  }
  return amountFromBillString(bd.tax) > 0;
};

const fmtRs = (n) => {
  if (n === "" || n == null) return "";
  const num = typeof n === "number" ? n : parseFloat(n);
  if (Number.isNaN(num)) return "";
  return `Rs. ${formatNum(num)}`;
};

function SkeletonBar({
  width = "100%",
  height = 10,
  className = "",
  fullWidth = true,
}) {
  const widthCss = typeof width === "number" ? `${width}px` : width;
  return (
    <div
      className={
        fullWidth ? styles.skeletonWrap : styles.skeletonWrapInline
      }
    >
      <span
        className={`${styles.skeletonInner} ${className}`}
        style={{
          width: widthCss,
          height,
          minHeight: height,
        }}
      />
    </div>
  );
}

const PdfTemplate = ({
  ReceiptData,
  invStyle: _invStyle = "style1",
  invTheme: _invTheme = PdfColorPallete?.[0],
  isInvoice: _isInvoice = false,
}) => {
  const [I18n] = useContext(LanguageContext);
  const {
    BusinessDetails,
    CustomerDetails,
    ItemDetails,
    Signature,
    BillDetails,
    state,
    accountInfo,
    isVerifiedAccountForWallet,
  } = ReceiptData || {};

  const raastDetail = accountInfo?.raast_info?.raast;

  const bName = BusinessDetails?.bName;
  const bNum = BusinessDetails?.bNum;
  const bAdd = BusinessDetails?.bAdd;
  const bImg = BusinessDetails?.bImg;

  const cName = CustomerDetails?.cName;
  const cPhone = CustomerDetails?.cPhone;
  const invoiceNum = CustomerDetails?.invoiceNum;
  const invDate = CustomerDetails?.date;
  const dueDate = CustomerDetails?.dueDate;
  const poRef = state?.po;

  const logoLetters = hasText(bName)
    ? bName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 4)
    : "";

  const items = Array.isArray(ItemDetails) ? ItemDetails : [];
  const hasItems = items.length > 0;
  const hasBreakdownAboveTotal =
    hasItems && (hasBillTax(BillDetails) || hasBillDiscount(BillDetails));
  const showBankCard = raastDetail && isVerifiedAccountForWallet && ReceiptData?.documentType == "in" ;
  const documentType = ReceiptData?.documentType ?? "in";
  const isPurchaseOrder = documentType === "out";
  const documentTitle = isPurchaseOrder
    ? I18n.purchase_order
    : I18n.invoice;

  return (
    <Box className={`mainInvoice ${styles.invoiceCard}`}>
      <header className={styles.headerRow}>
        <h1 className={styles.invoiceTitle}>{documentTitle}</h1>
        <Box className={styles.logoCircle} aria-hidden>
          {bImg ? (
            <img src={bImg} alt="" />
          ) : (
            <span className={styles.logoText}>{logoLetters || "—"}</span>
          )}
        </Box>
      </header>

      <Box className={styles.invoiceHeader}>
        <Box className={styles.headerColumn}>
          <HeadingText tag="h2">{I18n.from}</HeadingText>
        </Box>
        <Box className={styles.headerColumn}>
          <HeadingText tag="h2">{I18n.to}</HeadingText>
        </Box>
        <Box className={styles.headerColumn}>
          <HeadingText tag="h2">{I18n.details}</HeadingText>
        </Box>
      </Box>
      <Box className={styles.invoiceBody}>
        <Box className={styles.headerColumn}>
          {hasText(bName) ? (
            <ParagraphText className={styles.bodyLine}>{bName}</ParagraphText>
          ) : (
            <SkeletonBar height={10} width="92%" />
          )}
          {hasText(bNum) ? (
            <ParagraphText className={styles.bodyLine}>{bNum}</ParagraphText>
          ) : (
            <SkeletonBar height={10} width="72%" />
          )}
          {hasText(bAdd) ? (
            <ParagraphText className={styles.bodyLine}>{bAdd}</ParagraphText>
          ) : (
            <SkeletonBar height={10} width="88%" />
          )}
        </Box>
        <Box className={styles.headerColumn}>
          {hasText(cName) ? (
            <ParagraphText className={styles.bodyLine}>{cName}</ParagraphText>
          ) : (
            <SkeletonBar height={10} width="60%" />
          )}
          {hasText(cPhone) ? (
            <ParagraphText className={styles.bodyLine}>{cPhone}</ParagraphText>
          ) : (
            <SkeletonBar height={10} width="85%" />
          )}
        </Box>
        <Box className={styles.headerColumn}>
          {hasText(invoiceNum) ? (
            <ParagraphText className={`${styles.bodyLine} ${styles.poRef}`}>
              <span className={styles.poRefLabel}>{isPurchaseOrder ? I18n.purchase_no : I18n.invoice_no}.</span>
              <span className={styles.poRefValue}>{invoiceNum}</span>
            </ParagraphText>
          ) : (
            <SkeletonBar height={10} width="88%" />
          )}
          <ParagraphText className={`${styles.bodyLine} ${styles.poRef}`}>
            <span className={styles.poRefLabel}>{I18n.ref_no}</span>
            <span className={styles.poRefValue}>
              {!isRefEmpty(poRef) ? (
                formatRefDisplay(poRef)
              ) : (
                <SkeletonBar height={10} width={72} fullWidth={false} />
              )}
            </span>
          </ParagraphText>
        </Box>
      </Box>

      <table className={styles.itemsTable}>
        <thead>
          <tr>
            <th>{I18n.item_name}</th>
            <th>{I18n.quantity}</th>
            <th>{I18n.unit_price}</th>
            <th>{I18n.amount}</th>
          </tr>
        </thead>
        <tbody>
          {hasItems
            ? items.map((row, indx) => {
              const vals = Object.values(row);
              const name = vals[0];
              const qty = vals[1];
              const unit = vals[2];
              const amt = vals[3];
              return (
                <tr key={indx}>
                  <td>
                    <ParagraphText className={styles.cellText}>
                      {name ?? ""}
                    </ParagraphText>
                  </td>
                  <td>
                    <ParagraphText className={styles.cellText}>
                      {qty ?? ""}
                    </ParagraphText>
                  </td>
                  <td>
                    <ParagraphText className={styles.cellText}>
                      {fmtRs(unit) || unit}
                    </ParagraphText>
                  </td>
                  <td>
                    <ParagraphText className={styles.cellText}>
                      {fmtRs(amt) || amt}
                    </ParagraphText>
                  </td>
                </tr>
              );
            })
            : null}
          {!hasItems ? (
            <tr className={styles.skeletonRow}>
              <td>
                <span
                  className={`${styles.skeletonCell} ${styles.skeletonCellShort}`}
                />
              </td>
              <td>
                <span
                  className={`${styles.skeletonCell} ${styles.skeletonCellMid}`}
                />
              </td>
              <td>
                <span
                  className={`${styles.skeletonCell} ${styles.skeletonCellMid}`}
                />
              </td>
              <td>
                <span
                  className={`${styles.skeletonCell} ${styles.skeletonCellAmount}`}
                />
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <Box className={styles.breakdown}>
        {hasItems && (hasBillTax(BillDetails) || hasBillDiscount(BillDetails)) && <Box className={styles.breakdownRow}>
          <ParagraphText>{I18n.sub_total}</ParagraphText>
          <ParagraphText>
            {hasItems ? (
              fmtRs(BillDetails?.subT)
            ) : (
              <SkeletonBar height={10} width={72} fullWidth={false} />
            )}
          </ParagraphText>
        </Box>}
        {hasItems && hasBillDiscount(BillDetails) ? (
          <Box className={styles.breakdownRow}>
            <ParagraphText>{I18n.discount}</ParagraphText>
            <ParagraphText>{BillDetails?.disc ?? "—"}</ParagraphText>
          </Box>
        ) : null}
        {hasItems && hasBillTax(BillDetails) ? (
          <Box className={styles.breakdownRow}>
            <ParagraphText>{I18n.tax}</ParagraphText>
            <ParagraphText>{BillDetails?.tax ?? "—"}</ParagraphText>
          </Box>
        ) : null}

        <Box
          className={`${styles.breakdownRow} ${
            hasBreakdownAboveTotal ? styles.advancePaidRow : ""
          }`}
        >
          <ParagraphText>{I18n.total}</ParagraphText>
          <ParagraphText>{hasItems ? (
            fmtRs(BillDetails?.total)
          ) : (
            <SkeletonBar height={10} width={72} fullWidth={false} />
          )}
          </ParagraphText>
        </Box>

        {hasItems && BillDetails?.advance > 0 ? (
          <Box className={styles.breakdownRow}>
            <ParagraphText>{I18n.payment_received}</ParagraphText>
            <ParagraphText>{BillDetails?.advance ?? "—"}</ParagraphText>
          </Box>
        ) : null}

        {hasItems && BillDetails?.advance > 0 ? (
          <Box className={`${styles.breakdownRow} ${styles.advancePaidRow}`}>
            <ParagraphText>{I18n.amount_due}</ParagraphText>
            <ParagraphText>{formatNum(BillDetails?.total - BillDetails?.advance) ?? "—"}</ParagraphText>
          </Box>
        ) : null}


      </Box>

      <Box className={styles.termsMemoRow}>
        <Box className={styles.termsMemoHeader}>
          <Box className={styles.termsMemoHeaderItem}>
            <h3 className={styles.subSectionTitle}>{I18n.invoice_terms_label}</h3>
          </Box>
          <Box className={styles.termsMemoHeaderItem}>
            <h3 className={styles.subSectionTitle}>{I18n.note}</h3>
          </Box>
        </Box>
        <Box className={styles.termsMemoContent}>
          <Box className={styles.termsMemoContentItem}>
            <Box className={styles.termsInner}>
              <Box className={styles.detailRow}>
                <ParagraphText className={styles.detailLabel}>{isPurchaseOrder ? I18n.purchase_date : I18n.invoice_date}</ParagraphText>
                {hasText(invDate) ? (
                  <ParagraphText className={styles.detailValue}>{invDate}</ParagraphText>
                ) : (
                  <SkeletonBar height={10} width={100} fullWidth={false} />
                )}
              </Box>
              <Box className={styles.detailRow}>
                <ParagraphText className={styles.detailLabel}>{I18n.due_date}</ParagraphText>
                {hasText(dueDate) ? (
                  <ParagraphText className={styles.detailValue}>{dueDate}</ParagraphText>
                ) : (
                  <SkeletonBar height={10} width={100} fullWidth={false} />
                )}
              </Box>
            </Box>
          </Box>
          <Box className={styles.termsMemoContentItem}>
            {hasText(state?.note) ? (
              <ParagraphText className={styles.memoText}>{state.note}</ParagraphText>
            ) : (
              <Box className={styles.memoSkeletonStack}>
                <SkeletonBar height={10} width="100%" />
                <SkeletonBar height={10} width="78%" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box className={styles.footerRow}>
        <Box className={styles.footerLeft}>
          {showBankCard ? (
            <Box className={styles.bankCard}>
              <h3>{I18n.bank_account_details}</h3>
              <Box className={styles.bankRow}>
                <ParagraphText>{I18n.bank}:</ParagraphText>
                <ParagraphText>{raastDetail?.bank_name || ""}</ParagraphText>
              </Box>
              <Box className={styles.bankRow}>
                <ParagraphText>{I18n.account_title}:</ParagraphText>
                <ParagraphText>{raastDetail?.raast_details?.name || ""}</ParagraphText>
              </Box>
              <Box className={styles.bankRow}>
                <ParagraphText>{I18n.account_number}:</ParagraphText>
                <ParagraphText>{raastDetail?.raast_details?.account_number || ""}</ParagraphText>
              </Box>
            </Box>
          ) : (
            <>
              <SkeletonBar height={14} width="92%" />
              <Box style={{ marginTop: 10 }} />
              <SkeletonBar height={14} width="88%" />
            </>
          )}
        </Box>

        <Box className={styles.footerRight}>
          <Box className={styles.signatureBlock}>
            <Box className={styles.signatureLineArea}>
              {state?.isSignature &&
                (Signature?.type === "text" ? (
                  <ParagraphText className={styles.signatureText}>
                    {Signature?.value}
                  </ParagraphText>
                ) : Signature?.type === "img" ? (
                  <img
                    className={styles.signatureImg}
                    src={getSignImage(Signature?.value)}
                    alt=""
                  />
                ) : null)}
            </Box>
            <Box className={styles.signatureLineAreaBottom}>
              {/* auto: in Urdu the colon goes on the left; in a left-to-right line it sat on the
                  right, pressed against the first letter (د) and hiding it */}
              <ParagraphText className={styles.signatureLabel} dir="auto">
                {I18n.signature}:
              </ParagraphText>
              <span className={styles.signatureLine} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PdfTemplate;
